/* eslint-disable no-empty */
import {
  KnowledgeBaseArticle,
  KnowledgeBaseCategory,
  RequestBasePostKnowledgeBaseArticleUpdateCategory,
  RequestBasePostPutKnowledgeBaseArticle,
  RequestBasePostPutKnowledgeBaseCategory,
  RequestBasePutKnowledgeBaseCategoryNavigation,
  ResponseBaseGetKnowledgeBaseArticle,
  ResponseBaseGetKnowledgeBaseCategories,
  ResponseBaseGetKnowledgeBaseCategory,
  ResponseBaseGetKnowledgeBaseSearch,
  ResponseBasePostKnowledgeBaseArticleUpdateCategory,
} from '@hefe/models';
import { createApi } from '@reduxjs/toolkit/query/react';
import API from '../../../api/api';

export const knowledgeBaseApi = createApi({
  reducerPath: 'apiKnowledgeBase',
  baseQuery: API.baseQuery,
  tagTypes: [
    'KnowledgeBaseCategory',
    'KnowledgeBaseArticle',
    'KnowledgeBaseTag',
  ],
  endpoints: (builder) => ({
    /**************************************************************************
     *                              ARTICLES
     *************************************************************************/
    fetchTopArticles: builder.query<KnowledgeBaseArticle[], void>({
      query: (request) => '/knowledge-base/articles/top',
    }),
    fetchLatestArticles: builder.query<KnowledgeBaseArticle[], void>({
      query: (request) => '/knowledge-base/articles/latest',
    }),
    fetchArticle: builder.query<ResponseBaseGetKnowledgeBaseArticle, number>({
      query: (request) => `/knowledge-base/articles/${request}`,
      providesTags: (result, error, id) => [
        { type: 'KnowledgeBaseArticle', id: result?.id },
      ],
    }),
    createArticle: builder.mutation<
      ResponseBaseGetKnowledgeBaseArticle,
      RequestBasePostPutKnowledgeBaseArticle
    >({
      query: (request) => ({
        url: '/knowledge-base/articles',
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: ResponseBaseGetKnowledgeBaseArticle) =>
        response,
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            knowledgeBaseApi.util.updateQueryData(
              'fetchCategory',
              responseData?.category_id ?? 0,
              (draft) => {
                if (draft) {
                  draft.articles?.push(responseData);
                }
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),
    editArticle: builder.mutation<
      ResponseBaseGetKnowledgeBaseArticle,
      {
        id: number;
        request: RequestBasePostPutKnowledgeBaseArticle;
      }
    >({
      query: (request) => ({
        url: `/knowledge-base/articles/${request.id}`,
        method: 'PUT',
        body: request.request,
      }),
      transformResponse: (response: ResponseBaseGetKnowledgeBaseArticle) =>
        response,
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            knowledgeBaseApi.util.updateQueryData(
              'fetchCategory',
              responseData?.category_id ?? 0,
              (draft) => {
                if (draft) {
                  draft.articles = draft.articles?.map((item) =>
                    item.id === request.id ? responseData : item
                  );
                }
              }
            )
          );
          dispatch(
            knowledgeBaseApi.util.updateQueryData(
              'fetchArticle',
              responseData?.id,
              (draft: any) => {
                if (draft) {
                  Object.keys(responseData).forEach((key: any) => {
                    draft[key] = (responseData as any)[key];
                  });
                }
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),
    updateArticlesCategory: builder.mutation<
      ResponseBasePostKnowledgeBaseArticleUpdateCategory,
      {
        currentCategoryId: number;
        request: RequestBasePostKnowledgeBaseArticleUpdateCategory;
      }
    >({
      query: (request) => ({
        url: `/knowledge-base/articles/update-category`,
        method: 'POST',
        body: request.request,
      }),
      invalidatesTags: (result) => {
        const returnValue: any = [];
        result?.forEach((article) => {
          returnValue.push({
            type: 'KnowledgeBaseArticle',
            id: article.id,
          });

          returnValue.push({
            type: 'KnowledgeBaseCategory',
            id: article.category_id,
          });
        });

        return returnValue;
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            knowledgeBaseApi.util.updateQueryData(
              'fetchCategory',
              request?.currentCategoryId ?? 0,
              (draft) => {
                if (draft) {
                  draft.articles = draft.articles?.filter(
                    (item) => !request.request.article_ids.includes(item.id)
                  );
                }
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),
    uploadFile: builder.mutation<ResponseBaseGetKnowledgeBaseArticle, FormData>(
      {
        query: (request) => ({
          url: `/knowledge-base/articles/file`,
          method: 'POST',
          body: request,
          prepareHeaders: (headers: any) => {
            headers.set('Content-Type', 'multipart/form-data');
            return headers;
          },
        }),
        transformResponse: (response: ResponseBaseGetKnowledgeBaseArticle) =>
          response,
        async onQueryStarted(request, { dispatch, queryFulfilled }) {
          try {
            const { data: responseData } = await queryFulfilled;
            dispatch(
              knowledgeBaseApi.util.updateQueryData(
                'fetchArticle',
                responseData?.id,
                (draft: any) => {
                  if (draft) {
                    Object.keys(responseData).forEach((key: any) => {
                      draft[key] = (responseData as any)[key];
                    });
                  }
                }
              )
            );
          } catch (error: unknown) {}
        },
      }
    ),
    deleteArticle: builder.mutation<unknown, KnowledgeBaseArticle>({
      query: (article) => ({
        url: `/knowledge-base/articles/${article.id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(article, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            knowledgeBaseApi.util.updateQueryData(
              'fetchCategory',
              article.category_id ?? 0,
              (draft) => {
                if (draft) {
                  draft.articles = draft.articles?.filter(
                    (item) => item.id !== article.id
                  );
                }
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),
    deleteAttachment: builder.mutation<
      ResponseBaseGetKnowledgeBaseArticle,
      number
    >({
      query: (id) => ({
        url: `/knowledge-base/articles/file/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ResponseBaseGetKnowledgeBaseArticle) =>
        response,
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            knowledgeBaseApi.util.updateQueryData(
              'fetchArticle',
              responseData?.id,
              (draft: any) => {
                if (draft) {
                  Object.keys(responseData).forEach((key: any) => {
                    draft[key] = (responseData as any)[key];
                  });
                }
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),
    /**************************************************************************
     *                              CATEGORIES
     *************************************************************************/
    fetchNavigation: builder.query<
      ResponseBaseGetKnowledgeBaseCategories,
      void
    >({
      query: (request) => '/knowledge-base/categories/navigation',
    }),
    fetchCategory: builder.query<
      ResponseBaseGetKnowledgeBaseCategory,
      number | undefined
    >({
      query: (request) => `/knowledge-base/categories/${request}`,
      providesTags: (result, error, id) => [
        { type: 'KnowledgeBaseCategory', id: result?.id },
      ],
    }),
    addCategory: builder.mutation<
      KnowledgeBaseCategory,
      RequestBasePostPutKnowledgeBaseCategory
    >({
      query: (request) => ({
        url: `/knowledge-base/categories`,
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: KnowledgeBaseCategory) => response,
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            knowledgeBaseApi.util.updateQueryData(
              'fetchNavigation',
              undefined,
              (draft) => {
                draft?.push(responseData);
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),
    updateCategory: builder.mutation<
      ResponseBaseGetKnowledgeBaseCategory,
      {
        id: number;
        request: RequestBasePostPutKnowledgeBaseCategory;
      }
    >({
      query: (request) => ({
        url: `/knowledge-base/categories/${request.id}`,
        method: 'PUT',
        body: request.request,
      }),
      invalidatesTags: (result) => [
        { type: 'KnowledgeBaseCategory', id: result?.id },
      ],
      transformResponse: (response: ResponseBaseGetKnowledgeBaseCategory) =>
        response,
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            knowledgeBaseApi.util.updateQueryData(
              'fetchCategory',
              responseData?.id ?? 0,
              (draft) => {
                if (draft) {
                  draft.name = responseData?.name;
                }
              }
            )
          );

          dispatch(
            knowledgeBaseApi.util.updateQueryData(
              'fetchNavigation',
              undefined,
              (draft) => {
                const updateCategoryRecursive = (
                  categories: KnowledgeBaseCategory[]
                ) => {
                  categories.forEach((category) => {
                    if (category.id === responseData?.id) {
                      category.name = responseData?.name;
                    }
                    if (category.children) {
                      updateCategoryRecursive(category.children);
                    }
                  });
                };
                if (draft) {
                  updateCategoryRecursive(draft);
                }
              }
            )
          );
        } catch (error: unknown) {}
      },
    }),
    updateCategoryNavigation: builder.mutation<
      ResponseBaseGetKnowledgeBaseCategories,
      RequestBasePutKnowledgeBaseCategoryNavigation
    >({
      query: (request) => ({
        url: `/knowledge-base/categories/navigation`,
        method: 'PUT',
        body: request,
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            knowledgeBaseApi.util.updateQueryData(
              'fetchNavigation',
              undefined,
              () => responseData
            )
          );
        } catch (error: unknown) {}
      },
    }),
    deleteCategory: builder.mutation<
      ResponseBaseGetKnowledgeBaseCategories,
      number
    >({
      query: (id) => ({
        url: `/knowledge-base/categories/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        try {
          const { data: responseData } = await queryFulfilled;
          dispatch(
            knowledgeBaseApi.util.updateQueryData(
              'fetchNavigation',
              undefined,
              () => responseData
            )
          );
        } catch (error: unknown) {}
      },
    }),
    /**************************************************************************
     *                              SEARCH
     *************************************************************************/
    search: builder.query<ResponseBaseGetKnowledgeBaseSearch, string>({
      query: (text) => `/knowledge-base/search?q=${text}`,
    }),
  }), // end of endpoints
});

// Export hooks for usage in functional components
export const {
  useFetchNavigationQuery,
  useFetchLatestArticlesQuery,
  useFetchTopArticlesQuery,
  useFetchArticleQuery,
  useFetchCategoryQuery,
  useCreateArticleMutation,
  useEditArticleMutation,
  useUpdateArticlesCategoryMutation,
  useDeleteArticleMutation,
  useUploadFileMutation,
  useDeleteAttachmentMutation,
  useAddCategoryMutation,
  useUpdateCategoryMutation,
  useUpdateCategoryNavigationMutation,
  useDeleteCategoryMutation,
  useSearchQuery,
  reducerPath,
  reducer,
  middleware,
} = knowledgeBaseApi;
